import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "react-feather";

const FALLBACK_ROWS = 12;
const MIN_ROWS = 3;

/**
 * How many table rows fit in `ref`'s box without it scrolling: the box height
 * less the table's header and footer, divided by the tallest body row. Rows
 * in the full-screen view are single-line (see .pc-fit-body), so every row is
 * the same height and the answer is stable. Re-measured when the window (or
 * anything else) resizes the box.
 */
const useFitRowCount = (ref) => {
  const [rows, setRows] = useState(FALLBACK_ROWS);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const table = el.querySelector("table");
    const headH = table?.tHead?.offsetHeight ?? 40;
    const footH = table?.tFoot?.offsetHeight ?? 0;
    let rowH = 0;
    Array.from(table?.tBodies?.[0]?.rows || []).forEach((r) => {
      rowH = Math.max(rowH, r.offsetHeight);
    });
    if (!rowH) rowH = 34;
    // A table wider than a phone scrolls sideways; leave room for that bar.
    const scrollbar = el.scrollWidth > el.clientWidth ? 16 : 0;
    const fit = Math.floor((el.clientHeight - headH - footH - scrollbar - 2) / rowH);
    setRows(Math.max(MIN_ROWS, fit));
  }, [ref]);

  // After every render (setRows with the same number is a no-op), and on resize.
  useLayoutEffect(() => {
    measure();
  });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, measure]);

  return rows;
};

const chunk = (items, size) => {
  const pages = [];
  for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
  return pages;
};

const Pager = ({ page, pageCount, onPage, info }) => (
  <div className="pc-fit-pager">
    <span className="text-muted small">{info}</span>
    <div className="btn-group">
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => onPage(0)} title="First page">
        <ChevronsLeft size={15} />
      </button>
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => onPage(page - 1)} title="Previous page (Left arrow)">
        <ChevronLeft size={15} />
      </button>
      <span className="btn btn-sm btn-outline-secondary disabled text-dark">
        Page {page + 1} of {pageCount}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={page >= pageCount - 1}
        onClick={() => onPage(page + 1)}
        title="Next page (Right arrow)"
      >
        <ChevronRight size={15} />
      </button>
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => onPage(pageCount - 1)} title="Last page">
        <ChevronsRight size={15} />
      </button>
    </div>
  </div>
);

/**
 * A report cut into pages that exactly fill the space on screen, with the
 * pager underneath - the Overview's full-screen view, which never scrolls.
 * `split(items, rowsPerPage)` shapes the pages (the ledger keeps day
 * headings with their entries); plain slices otherwise. Left/Right arrow
 * keys turn the page.
 */
const FitPagedView = ({ items, split, renderPage, emptyText, noun = "rows" }) => {
  const ref = useRef(null);
  const rowsPerPage = useFitRowCount(ref);
  const pages = useMemo(() => (split ? split(items, rowsPerPage) : chunk(items, rowsPerPage)), [items, split, rowsPerPage]);
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, pages.length);
  const current = Math.min(page, pageCount - 1);

  useEffect(() => setPage(0), [items]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.("input, select, textarea")) return;
      if (e.key === "ArrowRight" || e.key === "PageDown") setPage((p) => Math.min(p + 1, pageCount - 1));
      if (e.key === "ArrowLeft" || e.key === "PageUp") setPage((p) => Math.max(p - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageCount]);

  const start = split ? null : current * rowsPerPage;
  const info = split
    ? `${items.length} ${noun}`
    : items.length
    ? `${start + 1}-${Math.min(items.length, start + rowsPerPage)} of ${items.length} ${noun}`
    : `0 ${noun}`;

  return (
    <div className="pc-fit">
      <div ref={ref} className="pc-fit-body">
        {items.length ? renderPage(pages[current] || []) : <div className="text-center text-muted py-5">{emptyText}</div>}
      </div>
      <Pager page={current} pageCount={pageCount} onPage={setPage} info={info} />
    </div>
  );
};

export default FitPagedView;
